/** Sorts a refused bank account mutation into the message the reader gets. */
import { CombinedGraphQLErrors } from '@apollo/client';
import { GraphQLErrorCode } from '@insio/validation';

export const BankAccountError = {
  INVALID: 'invalid',
  DUPLICATE: 'duplicate',
  INACTIVE: 'inactive',
  STATE_CONFLICT: 'stateConflict',
  SIGNER_IS_CREATOR: 'signerIsCreator',
  SIGNER_IS_FIRST_SIGNER: 'signerIsFirstSigner',
  SIGNER_IS_LAST_EDITOR: 'signerIsLastEditor',
  SIGNER_IS_STATEMENT_UPLOADER: 'signerIsStatementUploader',
  /** Someone else signed meanwhile; the list is read again. */
  STATE_CHANGED: 'stateChanged',
  EVIDENCE_STALE: 'evidenceStale',
  PERSON_REQUIRED: 'personRequired',
  NOT_PRINTED: 'notPrinted',
  SUPPLIER_NOT_LINKED: 'supplierNotLinked',
  OTHER: 'other'
} as const;

export type BankAccountError = (typeof BankAccountError)[keyof typeof BankAccountError];

const ERROR_BY_CODE: Readonly<Record<string, BankAccountError>> = {
  [GraphQLErrorCode.BANK_ACCOUNT_INVALID]: BankAccountError.INVALID,
  [GraphQLErrorCode.BANK_ACCOUNT_DUPLICATE]: BankAccountError.DUPLICATE,
  [GraphQLErrorCode.BANK_ACCOUNT_INACTIVE]: BankAccountError.INACTIVE,
  [GraphQLErrorCode.BANK_ACCOUNT_STATE_CONFLICT]: BankAccountError.STATE_CONFLICT,
  [GraphQLErrorCode.BANK_ACCOUNT_SIGNER_IS_CREATOR]: BankAccountError.SIGNER_IS_CREATOR,
  [GraphQLErrorCode.BANK_ACCOUNT_SIGNER_IS_FIRST_SIGNER]: BankAccountError.SIGNER_IS_FIRST_SIGNER,
  [GraphQLErrorCode.BANK_ACCOUNT_SIGNER_IS_LAST_EDITOR]: BankAccountError.SIGNER_IS_LAST_EDITOR,
  [GraphQLErrorCode.BANK_ACCOUNT_SIGNER_IS_STATEMENT_UPLOADER]:
    BankAccountError.SIGNER_IS_STATEMENT_UPLOADER,
  [GraphQLErrorCode.BANK_ACCOUNT_STATE_CHANGED]: BankAccountError.STATE_CHANGED,
  [GraphQLErrorCode.BANK_ACCOUNT_EVIDENCE_STALE]: BankAccountError.EVIDENCE_STALE,
  [GraphQLErrorCode.BANK_ACCOUNT_PERSON_REQUIRED]: BankAccountError.PERSON_REQUIRED,
  [GraphQLErrorCode.BANK_ACCOUNT_NOT_PRINTED]: BankAccountError.NOT_PRINTED,
  [GraphQLErrorCode.INVOICE_SUPPLIER_NOT_LINKED]: BankAccountError.SUPPLIER_NOT_LINKED
};

/** The first known refusal among the errors; anything else is OTHER. */
export function bankAccountError(error: unknown): BankAccountError {
  if (!CombinedGraphQLErrors.is(error)) return BankAccountError.OTHER;
  for (const gqlError of error.errors) {
    const code = gqlError.extensions?.['code'];
    if (typeof code === 'string' && Object.hasOwn(ERROR_BY_CODE, code)) return ERROR_BY_CODE[code];
  }
  return BankAccountError.OTHER;
}

/** Refusals that mean the list shows the account as it no longer is: it is read again. */
const STALE_VIEW_REFUSALS: readonly BankAccountError[] = [
  BankAccountError.STATE_CHANGED,
  BankAccountError.STATE_CONFLICT,
  BankAccountError.INACTIVE
];

export function isStaleViewRefusal(refusal: BankAccountError): boolean {
  return STALE_VIEW_REFUSALS.includes(refusal);
}

/** The `@insio/banking` validation codes a BANK_ACCOUNT_INVALID refusal names; empty for any other. */
export function bankingErrorCodes(error: unknown): string[] {
  if (!CombinedGraphQLErrors.is(error)) return [];
  const invalid = error.errors.find(
    gqlError => gqlError.extensions?.['code'] === GraphQLErrorCode.BANK_ACCOUNT_INVALID
  );
  const codes: unknown = invalid?.extensions?.['bankingErrors'];
  return Array.isArray(codes)
    ? codes.filter((code): code is string => typeof code === 'string')
    : [];
}
